import { BlockComponent } from "../../../framework/src/BlockComponent";
import { runEngine } from "../../../framework/src/RunEngine";
import { Message } from "../../../framework/src/Message";
import { IBlock } from "../../../framework/src/IBlock";
import MessageEnum, {
  getName,
} from "../../../framework/src/Messages/MessageEnum";

// Customizable Area Start
import { makeApiMessage as MakeApiMessage } from "../../../components/src/common";

export const configJSON = require("./config");

export interface IBannerForm {
  title: string;
  is_active: boolean;
  position: string;
  is_navigable: boolean;
  navigation_type: string;
  navigation_id: string;
  external_url: string;
  start_date: string;
  end_date: string;
  image_en: File | null;
  image_ar: File | null;
}
// Customizable Area End

export interface Props {
  // Customizable Area Start
  navigation: {
    navigate: Function;
    getParam: Function;
    goBack: Function;
  };
  id: string;
  // Customizable Area End
}

export interface S {
  // Customizable Area Start
  form: IBannerForm;
  imageEnPreview: string;
  imageArPreview: string;
  isSubmitting: boolean;
  errors: Record<string, string>;
  // Customizable Area End
}

export interface SS {}

class BannerCreateController extends BlockComponent<Props, S, SS> {
  // Customizable Area Start
  createBannerMessageId: string = "";
  // Customizable Area End

  constructor(props: Props) {
    super(props);
    // Customizable Area Start
    this.subScribedMessages = [
      getName(MessageEnum.RestAPIResponceMessage),
      getName(MessageEnum.RestAPIResponceSuccessMessage),
      getName(MessageEnum.RestAPIResponceDataMessage),
    ];

    this.receive = this.receive.bind(this);
    runEngine.attachBuildingBlock(this as IBlock, this.subScribedMessages);
    // Customizable Area End
  }

  async receive(from: string, message: Message) {
    // Customizable Area Start
    const requestId = message.getData(
      getName(MessageEnum.RestAPIResponceDataMessage)
    );

    const successResponse = message.getData(
      getName(MessageEnum.RestAPIResponceSuccessMessage)
    );

    if (requestId === this.createBannerMessageId) {
      this.setState({ isSubmitting: false });
      if (successResponse && successResponse.data) {
        this.props.navigation.navigate("BannerList");
      }
    }
    // Customizable Area End
  }

  // Customizable Area Start
  handleFormChange = (field: keyof IBannerForm, value: any) => {
    const updatedForm = { ...this.state.form, [field]: value };

    if (field === "is_navigable" && !value) {
      updatedForm.navigation_type = "";
      updatedForm.navigation_id = "";
      updatedForm.external_url = "";
    }

    if (field === "navigation_type" && value !== "external_url") {
      updatedForm.external_url = "";
    }

    if (field === "navigation_type" && value === "external_url") {
      updatedForm.navigation_id = "";
    }

    this.setState({ form: updatedForm });
  };

  handleImageChange = (
    field: "image_en" | "image_ar",
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const previewKey =
      field === "image_en" ? "imageEnPreview" : "imageArPreview";

    const reader = new FileReader();
    reader.onload = (e) => {
      this.setState({
        form: { ...this.state.form, [field]: file },
        [previewKey]: e.target?.result as string,
      } as Pick<S, "form" | "imageEnPreview" | "imageArPreview">);
    };
    reader.readAsDataURL(file);
  };

  validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    const { form } = this.state;

    if (!form.title.trim()) {
      errors.title = "Title is required";
    }

    if (!form.image_en) {
      errors.image_en = "English image is required";
    }

    if (!form.image_ar) {
      errors.image_ar = "Arabic image is required";
    }

    if (form.is_navigable) {
      if (!form.navigation_type) {
        errors.navigation_type = "Navigation type is required";
      }
      if (
        form.navigation_type === "external_url" &&
        !form.external_url.trim()
      ) {
        errors.external_url = "External URL is required";
      }
      if (
        ["product", "category", "screen"].includes(form.navigation_type) &&
        !form.navigation_id.trim()
      ) {
        errors.navigation_id = "Navigation ID is required";
      }
    }

    this.setState({ errors });
    return Object.keys(errors).length === 0;
  };

  handleSubmit = () => {
    if (!this.validateForm()) return;

    this.setState({ isSubmitting: true });

    const { form } = this.state;
    const formData = new FormData();

    formData.append("title", form.title);
    formData.append("is_active", String(form.is_active));
    formData.append("position", form.position || "0");
    formData.append("is_navigable", String(form.is_navigable));

    if (form.is_navigable && form.navigation_type) {
      formData.append("navigation_type", form.navigation_type);
      if (form.navigation_type === "external_url") {
        formData.append("external_url", form.external_url);
      } else if (form.navigation_id) {
        formData.append("navigation_id", form.navigation_id);
      }
    }

    if (form.start_date) {
      formData.append("start_date", form.start_date);
    }
    if (form.end_date) {
      formData.append("end_date", form.end_date);
    }

    if (form.image_en) {
      formData.append("image_en", form.image_en);
    }
    if (form.image_ar) {
      formData.append("image_ar", form.image_ar);
    }

    const requestMessage = MakeApiMessage({
      url: configJSON.ApiUrls.bannersList,
      method: "POST",
      body: formData,
      isHeader: true,
    });

    this.createBannerMessageId = requestMessage.messageId;
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  handleCancel = () => {
    this.props.navigation.navigate("BannerList");
  };
  // Customizable Area End
}

export default BannerCreateController;
